import React from 'react'
import { isEmpty } from 'lodash'
import Table from 'antd/lib/table'
import Switch from 'antd/lib/switch'
import Tooltip from 'antd/lib/tooltip'
import { QuestionCircleOutlined } from '@ant-design/icons'
import SnoozeButton from './SnoozeButton'
import CapacityUseColumnTitle from './CapacityUseColumnTitle'
import CapacityUseCell from './CapacityUseCell'
import { getAllOutOfStockGroups, resolveEffectiveCapacity } from './utils'
import { StoreModifier, ModifierGroup } from '../types'
import { isAdmin, isManager, useSession } from 'packages/@slerp/accounts'
import { AvailabilityAction } from './types'
import { Cell, TableContainer, WrappText } from './styles'
import { CommonTag } from 'components/Common'

interface ModifiersInventoryTableProps {
  storeModifiers: StoreModifier[]
  isPreorder?: boolean
  isLoading: boolean
  onToggle: (action: AvailabilityAction, id: string, name: string) => void
  processingModifierStockToggle: string[]
  isModifierTogglingStockState: boolean
  snoozingStoreModifier: boolean
  unsnoozingStoreModifier: boolean
  selectedRowKeys: string[]
  onSelectedRowKeysChange: (keys: any) => void
  modifierGroups: ModifierGroup[]
  modifierCapacityDefaults: { pickup: number | null; delivery: number | null }
}

const ModifiersInventoryTable = ({
  storeModifiers,
  isPreorder,
  isLoading,
  onToggle,
  processingModifierStockToggle,
  isModifierTogglingStockState,
  snoozingStoreModifier,
  unsnoozingStoreModifier,
  selectedRowKeys,
  onSelectedRowKeysChange,
  modifierGroups,
  modifierCapacityDefaults
}: ModifiersInventoryTableProps) => {
  const { user } = useSession()
  const canManage = isAdmin(user) || isManager(user)

  const allOutOfStockGroups = React.useMemo(
    () =>
      getAllOutOfStockGroups(
        storeModifiers.map((sm) => ({
          in_stock: sm.in_stock,
          availability_group: sm.modifier?.availability_group
        }))
      ),
    [storeModifiers]
  )

  const STORE_MODIFIER_COLUMNS = [
    {
      title: 'Modifier Name',
      dataIndex: ['modifier', 'name'],
      key: 'modifier_name'
    },
    {
      title: 'SKU',
      dataIndex: ['modifier', 'sku'],
      key: 'modifier_sku',
      width: '15%',
      render: (sku: string) => <WrappText>{sku}</WrappText>
    },
    {
      title: 'Availability group',
      key: 'availability_group',
      width: '12%',
      sorter: (a: StoreModifier, b: StoreModifier) =>
        (a.modifier?.availability_group || '').localeCompare(
          b.modifier?.availability_group || ''
        ),
      render: (record: StoreModifier) => {
        const group = record.modifier?.availability_group
        return (
          group && (
            <Cell>
              <CommonTag
                style={{ margin: '0 auto' }}
                type={allOutOfStockGroups.has(group) ? 'active' : 'default'}
              >
                {group}
              </CommonTag>
            </Cell>
          )
        )
      }
    },
    {
      title: <CapacityUseColumnTitle title='Pickup capacity use' />,
      key: 'pickup_capacity_use',
      align: 'center' as const,
      render: (record: StoreModifier) => {
        const groups = record.modifier.modifier_group_modifiers.map((mgm) => {
          const group = modifierGroups.find(
            (g) => g.id === mgm.modifier_group_id
          )
          return { name: group?.name || '', weight: group?.capacity_weight }
        })
        const capacity = resolveEffectiveCapacity(
          record.modifier.capacity_weight,
          'Modifier',
          groups,
          'Modifier Group',
          modifierCapacityDefaults.pickup
        )
        return <CapacityUseCell capacity={capacity} />
      }
    },
    {
      title: <CapacityUseColumnTitle title='Delivery capacity use' />,
      key: 'delivery_capacity_use',
      align: 'center' as const,
      render: (record: StoreModifier) => {
        const groups = record.modifier.modifier_group_modifiers.map((mgm) => {
          const group = modifierGroups.find(
            (g) => g.id === mgm.modifier_group_id
          )
          return { name: group?.name || '', weight: group?.capacity_weight }
        })
        const capacity = resolveEffectiveCapacity(
          record.modifier.capacity_weight,
          'Modifier',
          groups,
          'Modifier Group',
          modifierCapacityDefaults.delivery
        )
        return <CapacityUseCell capacity={capacity} />
      }
    },
    {
      title: 'In Stock',
      dataIndex: 'in_stock',
      key: 'in_stock',
      width: 70,
      align: 'center' as const,
      render: (
        inStock: boolean,
        { id, modifier_id, unsnooze_at, modifier }: StoreModifier
      ) => {
        const loading = processingModifierStockToggle.includes(modifier_id)
        return (
          <Switch
            loading={loading}
            checked={inStock}
            disabled={
              !canManage ||
              isModifierTogglingStockState ||
              !isEmpty(unsnooze_at)
            }
            onChange={(value: boolean) =>
              onToggle(
                value
                  ? AvailabilityAction.IN_STOCK
                  : AvailabilityAction.OUT_OF_STOCK,
                id,
                modifier.name
              )
            }
            data-testid={`modifiers-${modifier.name}-in-stock-switch`}
          />
        )
      }
    }
  ]

  const MODIFIER_SNOOZE_COLUMN = canManage
    ? [
        {
          title: (
            <>
              <span>Snooze</span>
              <Tooltip
                title={
                  <span>
                    Modifier snooze allows you to mark a product temporarily out
                    of stock. For more information read{' '}
                    <a
                      href='https://support.slerp.com/en_us/preview/article/modifier-snooze-Bkjm7RHn5'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      here
                    </a>
                    .
                  </span>
                }
              >
                <QuestionCircleOutlined className='_ml-8 _mr-8' />
              </Tooltip>
            </>
          ),
          dataIndex: 'snooze',
          key: 'snooze',
          width: 95,
          align: 'center' as const,
          render: (
            val: undefined | null,
            { id, modifier, unsnooze_at }: StoreModifier
          ) => {
            return (
              <SnoozeButton
                key={id}
                itemId={id}
                name={modifier.name}
                unsnoozeAt={unsnooze_at}
                onClickHandler={onToggle}
                loading={snoozingStoreModifier || unsnoozingStoreModifier}
                disabled={snoozingStoreModifier || unsnoozingStoreModifier}
                dataTestId='inventory-dashboard-snooze-modifier'
              />
            )
          }
        }
      ]
    : []

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectedRowKeysChange
  }

  return (
    <TableContainer>
      <Table
        loading={isLoading}
        data-testid='inventory-dashboard-modifiers-inventory-table'
        rowSelection={rowSelection as any}
        columns={
          !isPreorder
            ? [...STORE_MODIFIER_COLUMNS, ...MODIFIER_SNOOZE_COLUMN]
            : STORE_MODIFIER_COLUMNS
        }
        dataSource={storeModifiers}
        scroll={{ y: 'calc(90vh - 55px)' }}
        rowKey='id'
      />
    </TableContainer>
  )
}

export default ModifiersInventoryTable
